<!-- eslint-disable vue/no-bare-strings-in-template, vue/no-static-inline-styles, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const isLoading = ref(true);
const agents = ref([]);
const entitlements = computed(() => store.getters['liaAgents/getEntitlements']);
const availableVersions = computed(
  () => store.getters['liaAgents/getAvailableVersions']
);
const canCreateAnyAgent = computed(() => {
  return availableVersions.value.some(v => v.can_create);
});

const showcaseRef = ref(null);

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const fetchAgents = async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('liaAgents/fetch'),
      store.dispatch('liaAgents/fetchEntitlements'),
    ]);
    agents.value = store.getters['liaAgents/getAgents'];
  } catch (error) {
    useAlert(t('LIA_AGENTS.LIST.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchAgents();
});

const createAgent = () => {
  router.push(accountScopedRoute('lia_agents_create'));
};

const editAgent = agent => {
  router.push(accountScopedRoute('lia_agents_edit', { agentId: agent.id }));
};

const showDeleteModal = ref(false);
const deleteTarget = ref(null);
const deletePassword = ref('');
const isDeleting = ref(false);

const openDeleteModal = agent => {
  deleteTarget.value = agent;
  deletePassword.value = '';
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  deleteTarget.value = null;
  deletePassword.value = '';
};

const confirmDelete = async () => {
  if (!deletePassword.value || !deleteTarget.value) return;
  isDeleting.value = true;
  try {
    await store.dispatch('liaAgents/delete', {
      id: deleteTarget.value.id,
      password: deletePassword.value,
    });
    useAlert(t('LIA_AGENTS.LIST.DELETE_SUCCESS'));
    closeDeleteModal();
    fetchAgents();
  } catch (error) {
    useAlert(error.message || t('LIA_AGENTS.LIST.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

const toggleAgent = async agent => {
  try {
    if (agent.is_active) {
      await store.dispatch('liaAgents/deactivate', agent.id);
    } else {
      await store.dispatch('liaAgents/activate', agent.id);
    }
    useAlert(
      agent.is_active
        ? t('LIA_AGENTS.LIST.DEACTIVATED')
        : t('LIA_AGENTS.LIST.ACTIVATED')
    );
    fetchAgents();
  } catch (error) {
    useAlert(t('LIA_AGENTS.LIST.TOGGLE_ERROR'));
  }
};

// Follow-up automático (Lia::FollowUpJob): a IA volta a chamar o lead que
// parou de responder. Liga/desliga por agente; o job só age com enabled=true.
const followUpEnabled = agent => agent.settings?.follow_up?.enabled === true;

const toggleFollowUp = async agent => {
  const enabling = !followUpEnabled(agent);
  const settings = {
    ...(agent.settings || {}),
    follow_up: { ...(agent.settings?.follow_up || {}), enabled: enabling },
  };
  try {
    await store.dispatch('liaAgents/update', { id: agent.id, settings });
    useAlert(
      enabling
        ? t('LIA_AGENTS.LIST.FOLLOW_UP_ON')
        : t('LIA_AGENTS.LIST.FOLLOW_UP_OFF')
    );
    fetchAgents();
  } catch (error) {
    useAlert(t('LIA_AGENTS.LIST.TOGGLE_ERROR'));
  }
};

const getVersionData = slug => {
  return availableVersions.value.find(v => v.slug === slug);
};

const hasVersion = slug => {
  const v = getVersionData(slug);
  return v && v.available > 0;
};

const getAgentsForVersion = versionStr => {
  return agents.value.filter(a => {
    const v = a.lia_version || '';
    if (versionStr === '1.0') return !v || v === 'LIA 1.0' || v === '1.0';
    if (versionStr === '1.4') return v === 'LIA 1.4' || v === '1.4';
    if (versionStr === '2.1') return v.includes('2.1');
    if (versionStr === '3.1') return v.includes('3.1');
    return false;
  });
};

const licenseCards = [
  {
    slug: 'lia-1-0',
    name: 'LIA 1.0',
    subtitle: 'Incluso',
    version: '1.0',
    borderClass: 'border-green-200 dark:border-green-800/40',
    bgClass: 'bg-green-100 dark:bg-green-900/40',
    textClass: 'text-green-700 dark:text-green-300',
    barClass: 'bg-green-500',
    accentClass: 'text-green-600 dark:text-green-400',
  },
  {
    slug: 'lia-1-4',
    name: 'LIA 1.4',
    subtitle: 'R$ 97/mês',
    version: '1.4',
    borderClass: 'border-emerald-200 dark:border-emerald-800/40',
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/40',
    textClass: 'text-emerald-700 dark:text-emerald-300',
    barClass: 'bg-emerald-500',
    accentClass: 'text-emerald-600 dark:text-emerald-400',
  },
  {
    slug: 'lia-2-1',
    name: 'LIA 2.1',
    subtitle: 'R$ 697/mês',
    version: '2.1',
    borderClass: 'border-blue-200 dark:border-blue-800/40',
    bgClass: 'bg-blue-100 dark:bg-blue-900/40',
    textClass: 'text-blue-700 dark:text-blue-300',
    barClass: 'bg-blue-500',
    accentClass: 'text-blue-600 dark:text-blue-400',
  },
  {
    slug: 'lia-3-1',
    name: 'LIA 3.1',
    subtitle: 'R$ 997/mês',
    version: '3.1',
    borderClass: 'border-violet-200 dark:border-violet-800/40',
    bgClass: 'bg-violet-100 dark:bg-violet-900/40',
    textClass: 'text-violet-700 dark:text-violet-300',
    barClass: 'bg-violet-500',
    accentClass: 'text-violet-600 dark:text-violet-400',
  },
];
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col h-full bg-n-background">
    <!-- Header -->
    <header
      class="sticky top-0 z-20 px-6 py-4 border-b border-n-weak bg-n-solid-2/80 backdrop-blur-xl"
    >
      <div class="flex items-center justify-between max-w-7xl mx-auto">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12 tracking-tight">
            {{ t('LIA_AGENTS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11 mt-0.5">
            {{ t('LIA_AGENTS.DESCRIPTION') }}
          </p>
        </div>
        <button
          v-if="entitlements.has_access"
          class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl transition-all duration-200"
          :class="
            canCreateAnyAgent
              ? 'text-white bg-woot-600 hover:bg-woot-700 dark:bg-woot-500 dark:hover:bg-woot-600 shadow-sm'
              : 'text-n-slate-11 bg-n-alpha-2 cursor-not-allowed'
          "
          :disabled="!canCreateAnyAgent"
          @click="createAgent"
        >
          <span class="i-lucide-plus size-4" />
          {{ t('LIA_AGENTS.LIST.CREATE_BUTTON') }}
        </button>
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 overflow-auto">
      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center h-64">
        <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-11" />
      </div>

      <!-- ═══════════════════════════════════════════ -->
      <!--  NO LICENSE                                 -->
      <!-- ═══════════════════════════════════════════ -->
      <div
        v-else-if="!entitlements.has_access"
        class="max-w-6xl mx-auto px-6 py-16"
      >
        <!-- Hero -->
        <div class="text-center mb-20">
          <div
            class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400 text-sm font-medium mb-6"
          >
            <span class="i-lucide-sparkles size-4" />
            Inteligência Artificial
          </div>
          <h1
            class="text-5xl sm:text-6xl lg:text-7xl font-bold text-n-slate-12 tracking-tight leading-[1.1] mb-6"
          >
            Agentes LIA
          </h1>
          <p class="text-xl text-n-slate-11 max-w-2xl mx-auto leading-relaxed">
            Automatize seu atendimento com IA que entende, responde e resolve.
            Disponível 24 horas, 7 dias por semana.
          </p>
        </div>

        <!-- Metrics -->
        <div
          class="grid grid-cols-4 gap-px bg-n-weak rounded-2xl overflow-hidden mb-20"
        >
          <div
            v-for="m in [
              { v: '24/7', l: 'Disponibilidade' },
              { v: '<3s', l: 'Tempo de resposta' },
              { v: '90%', l: 'Resolução automática' },
              { v: '+50%', l: 'Aumento em vendas' },
            ]"
            :key="m.l"
            class="bg-n-solid-2 p-8 text-center"
          >
            <p class="text-3xl font-bold text-n-slate-12 mb-1">{{ m.v }}</p>
            <p class="text-sm text-n-slate-11">{{ m.l }}</p>
          </div>
        </div>

        <!-- Version Cards -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-20">
          <!-- LIA 1.4 -->
          <div
            class="rounded-2xl border border-n-weak bg-n-solid-2 p-8 flex flex-col"
          >
            <div class="flex items-center justify-between mb-6">
              <span
                class="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
              >
                INCLUSO
              </span>
            </div>
            <h3 class="text-2xl font-bold text-n-slate-12 mb-1">LIA 1.4</h3>
            <p class="text-sm text-n-slate-11 mb-6">Agente Inteligente</p>
            <p class="text-sm text-n-slate-11 leading-relaxed mb-8">
              Atendimento automatizado com IA conversacional. Responde
              perguntas, qualifica leads e transfere para humanos.
            </p>
            <ul class="space-y-3 mb-8 flex-1">
              <li
                v-for="f in [
                  'IA Conversacional avançada',
                  'Análise de imagens e áudios',
                  'Qualificação de leads',
                  'Transferência inteligente',
                ]"
                :key="f"
                class="flex items-center gap-3 text-sm text-n-slate-12"
              >
                <span
                  class="i-lucide-check size-4 text-green-500 flex-shrink-0"
                />
                {{ f }}
              </li>
            </ul>
            <div class="pt-6 border-t border-n-weak flex items-baseline gap-1">
              <span class="text-2xl font-bold text-n-slate-12">1 incluso</span>
              <span class="text-sm text-n-slate-11">+ R$ 97/extra</span>
            </div>
          </div>

          <!-- LIA 2.1 -->
          <div
            class="rounded-2xl border-2 border-woot-500 bg-n-solid-2 p-8 flex flex-col relative"
          >
            <div class="flex items-center justify-between mb-6">
              <span
                class="px-3 py-1 rounded-full text-xs font-semibold bg-woot-500 text-white"
              >
                POPULAR
              </span>
            </div>
            <h3 class="text-2xl font-bold text-n-slate-12 mb-1">LIA 2.1</h3>
            <p class="text-sm text-n-slate-11 mb-6">Agente com Agenda</p>
            <p class="text-sm text-n-slate-11 leading-relaxed mb-8">
              Tudo da LIA 1.4 com integração a calendários. Agenda, verifica
              disponibilidade e envia lembretes.
            </p>
            <ul class="space-y-3 mb-8 flex-1">
              <li
                v-for="f in [
                  'Tudo da LIA 1.4',
                  'Google Calendar integrado',
                  'Lembretes por WhatsApp',
                  'Reagendamento via chat',
                ]"
                :key="f"
                class="flex items-center gap-3 text-sm text-n-slate-12"
              >
                <span
                  class="i-lucide-check size-4 text-woot-500 flex-shrink-0"
                />
                {{ f }}
              </li>
            </ul>
            <div class="pt-6 border-t border-n-weak flex items-baseline gap-1">
              <span class="text-2xl font-bold text-n-slate-12">R$ 697</span>
              <span class="text-sm text-n-slate-11">/mês</span>
            </div>
          </div>

          <!-- LIA 3.1 -->
          <div
            class="rounded-2xl border border-n-weak bg-n-solid-2 p-8 flex flex-col"
          >
            <div class="flex items-center justify-between mb-6">
              <span
                class="px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300"
              >
                ENTERPRISE
              </span>
            </div>
            <h3 class="text-2xl font-bold text-n-slate-12 mb-1">LIA 3.1</h3>
            <p class="text-sm text-n-slate-11 mb-6">Agente Enterprise</p>
            <p class="text-sm text-n-slate-11 leading-relaxed mb-8">
              O mais poderoso. Integra com ERPs, CRMs e sistemas externos.
              Automação completa de processos.
            </p>
            <ul class="space-y-3 mb-8 flex-1">
              <li
                v-for="f in [
                  'Tudo da LIA 2.1',
                  'Integração com ERPs',
                  'Ações em tempo real',
                  'Multi-agente especializado',
                ]"
                :key="f"
                class="flex items-center gap-3 text-sm text-n-slate-12"
              >
                <span
                  class="i-lucide-check size-4 text-violet-500 flex-shrink-0"
                />
                {{ f }}
              </li>
            </ul>
            <div class="pt-6 border-t border-n-weak flex items-baseline gap-1">
              <span class="text-2xl font-bold text-n-slate-12">R$ 997</span>
              <span class="text-sm text-n-slate-11">/mês</span>
            </div>
          </div>
        </div>

        <!-- CTA -->
        <div class="rounded-2xl bg-n-slate-12 p-12 text-center">
          <h3 class="text-2xl font-bold text-white mb-3">
            Pronto para começar?
          </h3>
          <p class="text-n-slate-6 mb-6 max-w-lg mx-auto">
            Fale com nosso time para ativar seus agentes LIA e transformar seu
            atendimento.
          </p>
          <div
            class="flex items-center justify-center gap-6 text-sm text-n-slate-6"
          >
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-check-circle size-4 text-green-400" />
              Setup em 24h
            </span>
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-check-circle size-4 text-green-400" />
              Suporte dedicado
            </span>
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-check-circle size-4 text-green-400" />
              Sem fidelidade
            </span>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════ -->
      <!--  HAS LICENSE                                -->
      <!-- ═══════════════════════════════════════════ -->
      <template v-else>
        <div class="max-w-7xl mx-auto px-6 py-6">
          <!-- ────────────────────────────────────── -->
          <!-- 4 License Cards with agents inside     -->
          <!-- ────────────────────────────────────── -->
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div
              v-for="card in licenseCards"
              :key="card.slug"
              class="rounded-2xl border bg-n-solid-2 overflow-hidden flex flex-col transition-all duration-200"
              :class="
                hasVersion(card.slug)
                  ? card.borderClass
                  : 'border-n-weak opacity-60'
              "
            >
              <!-- Card Header -->
              <div class="p-4 pb-3">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold"
                      :class="[card.bgClass, card.textClass]"
                    >
                      {{ card.version }}
                    </div>
                    <div>
                      <p
                        class="text-sm font-semibold text-n-slate-12 leading-tight"
                      >
                        {{ card.name }}
                      </p>
                      <p class="text-[10px] text-n-slate-10">
                        {{ card.subtitle }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- License info -->
                <template v-if="hasVersion(card.slug)">
                  <div class="flex items-baseline gap-1 mb-1.5">
                    <span class="text-base font-bold" :class="card.accentClass">
                      {{ getVersionData(card.slug)?.used || 0 }}
                    </span>
                    <span class="text-[11px] text-n-slate-10">
                      / {{ getVersionData(card.slug)?.available || 0 }} licenças
                    </span>
                  </div>
                  <div
                    class="h-1 bg-n-alpha-2 rounded-full overflow-hidden mb-2"
                  >
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      :class="
                        (getVersionData(card.slug)?.used || 0) >=
                        (getVersionData(card.slug)?.available || 1)
                          ? 'bg-amber-500'
                          : card.barClass
                      "
                      :style="{
                        width: `${Math.min(((getVersionData(card.slug)?.used || 0) / Math.max(getVersionData(card.slug)?.available || 1, 1)) * 100, 100)}%`,
                      }"
                    />
                  </div>
                  <!-- Key -->
                  <div
                    v-if="getVersionData(card.slug)?.license_key"
                    class="px-2.5 py-1.5 bg-n-alpha-1 rounded-md mb-1"
                  >
                    <p
                      class="font-mono text-[10px] font-semibold text-n-slate-12 tracking-wider"
                    >
                      {{ getVersionData(card.slug).license_key }}
                    </p>
                    <p
                      v-if="getVersionData(card.slug).expires_at"
                      class="text-[9px] text-n-slate-10"
                    >
                      Expira
                      {{ formatDate(getVersionData(card.slug).expires_at) }}
                    </p>
                  </div>
                </template>

                <!-- No license -->
                <template v-else>
                  <div
                    class="flex items-center gap-1.5 py-3 justify-center text-n-slate-10"
                  >
                    <span class="i-lucide-lock size-3.5" />
                    <span class="text-[11px]">Sem licença</span>
                  </div>
                </template>
              </div>

              <!-- Agents list inside card -->
              <div class="flex-1 border-t border-n-weak">
                <template v-if="getAgentsForVersion(card.version).length > 0">
                  <div
                    v-for="(agent, idx) in getAgentsForVersion(card.version)"
                    :key="agent.id"
                    class="flex items-center gap-3 px-4 py-3"
                    :class="idx > 0 ? 'border-t border-n-weak' : ''"
                  >
                    <!-- Bot icon (left) -->
                    <span
                      class="i-lucide-bot size-5 flex-shrink-0 cursor-default"
                      :class="card.accentClass"
                      :title="agent.name"
                    />
                    <!-- Spacer -->
                    <div class="flex-1" />
                    <!-- Status dot + Actions (right) -->
                    <div class="flex items-center gap-2 flex-shrink-0">
                      <span
                        class="w-3 h-3 rounded-full flex-shrink-0 cursor-default"
                        :class="
                          agent.is_active ? 'bg-green-500' : 'bg-n-slate-9'
                        "
                        :title="agent.is_active ? 'Ativo' : 'Pausado'"
                      />
                      <button
                        class="flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
                        :class="
                          followUpEnabled(agent)
                            ? 'text-n-teal-11 bg-n-teal-3 hover:bg-n-teal-4'
                            : 'text-n-slate-11 hover:bg-n-alpha-3'
                        "
                        :title="
                          followUpEnabled(agent)
                            ? t('LIA_AGENTS.LIST.FOLLOW_UP_ON_TITLE')
                            : t('LIA_AGENTS.LIST.FOLLOW_UP_OFF_TITLE')
                        "
                        @click="toggleFollowUp(agent)"
                      >
                        <span class="i-lucide-alarm-clock size-5" />
                      </button>
                      <button
                        class="flex items-center justify-center w-9 h-9 rounded-lg text-n-slate-11 hover:bg-n-alpha-3 transition-colors"
                        title="Configurações"
                        @click="editAgent(agent)"
                      >
                        <span class="i-lucide-settings size-5" />
                      </button>
                      <button
                        class="flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
                        :class="
                          agent.is_active
                            ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                            : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                        "
                        :title="
                          agent.is_active ? 'Pausar agente' : 'Ativar agente'
                        "
                        @click="toggleAgent(agent)"
                      >
                        <span
                          :class="
                            agent.is_active ? 'i-lucide-pause' : 'i-lucide-play'
                          "
                          class="size-5"
                        />
                      </button>
                      <button
                        class="flex items-center justify-center w-9 h-9 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Excluir agente"
                        @click="openDeleteModal(agent)"
                      >
                        <span class="i-lucide-trash-2 size-5" />
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="px-4 py-3 text-center">
                    <p class="text-[11px] text-n-slate-10">
                      {{
                        hasVersion(card.slug) ? 'Nenhum agente' : 'Contratar'
                      }}
                    </p>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- ──────────────────────────────────────── -->
          <!-- Showcase (LIA 1.4, 2.1, 3.1 only)      -->
          <!-- ──────────────────────────────────────── -->
          <div ref="showcaseRef">
            <div class="flex items-center gap-4 mb-10">
              <div class="flex-1 h-px bg-n-weak" />
              <span class="text-sm font-semibold text-n-slate-11">
                Potencialize com versões avançadas
              </span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
              <!-- LIA 1.4 -->
              <div
                class="rounded-2xl border bg-n-solid-2 overflow-hidden"
                :class="[
                  hasVersion('lia-1-4')
                    ? 'border-emerald-300 dark:border-emerald-800/60'
                    : 'border-n-weak',
                ]"
              >
                <div
                  v-if="hasVersion('lia-1-4')"
                  class="px-6 py-2 bg-emerald-50 dark:bg-emerald-900/20 flex items-center gap-2"
                >
                  <span
                    class="i-lucide-check-circle size-4 text-emerald-600 dark:text-emerald-400"
                  />
                  <span
                    class="text-xs font-semibold text-emerald-700 dark:text-emerald-300"
                  >
                    Ativo no seu plano
                  </span>
                </div>
                <div class="p-6">
                  <div class="flex items-center justify-between mb-4">
                    <span
                      class="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                    >
                      START
                    </span>
                    <div class="text-right">
                      <span class="text-sm font-bold text-n-slate-12"
                        >R$ 97</span
                      >
                      <span class="text-xs text-n-slate-10">/mês cada</span>
                    </div>
                  </div>
                  <h3 class="text-xl font-bold text-n-slate-12 mb-1">
                    LIA 1.4
                  </h3>
                  <p class="text-sm text-n-slate-11 mb-6">Agente Start</p>

                  <ul class="space-y-2.5 mb-6">
                    <li
                      v-for="f in [
                        'IA Conversacional avançada',
                        'Análise de imagens e áudios',
                        'Qualificação inteligente de leads',
                        'Transferência para equipe certa',
                      ]"
                      :key="f"
                      class="flex items-start gap-2.5 text-sm text-n-slate-12"
                    >
                      <span
                        class="i-lucide-check size-4 text-emerald-500 mt-0.5 flex-shrink-0"
                      />
                      {{ f }}
                    </li>
                  </ul>

                  <div
                    class="flex flex-wrap gap-1.5 pt-5 border-t border-n-weak"
                  >
                    <span
                      v-for="u in ['E-commerce', 'Suporte', 'Vendas', 'SAC']"
                      :key="u"
                      class="px-2.5 py-1 text-[11px] font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ u }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- LIA 2.1 -->
              <div
                class="rounded-2xl border bg-n-solid-2 overflow-hidden"
                :class="[
                  hasVersion('lia-2-1')
                    ? 'border-blue-300 dark:border-blue-800/60'
                    : 'border-n-weak',
                ]"
              >
                <div
                  v-if="hasVersion('lia-2-1')"
                  class="px-6 py-2 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-2"
                >
                  <span
                    class="i-lucide-check-circle size-4 text-blue-600 dark:text-blue-400"
                  />
                  <span
                    class="text-xs font-semibold text-blue-700 dark:text-blue-300"
                  >
                    Ativo no seu plano
                  </span>
                </div>
                <div class="p-6">
                  <div class="flex items-center justify-between mb-4">
                    <span
                      class="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                    >
                      ADVANCE
                    </span>
                    <div class="text-right">
                      <span class="text-sm font-bold text-n-slate-12"
                        >R$ 697</span
                      >
                      <span class="text-xs text-n-slate-10">/mês</span>
                    </div>
                  </div>
                  <h3 class="text-xl font-bold text-n-slate-12 mb-1">
                    LIA 2.1
                  </h3>
                  <p class="text-sm text-n-slate-11 mb-6">Agente Advance</p>

                  <ul class="space-y-2.5 mb-6">
                    <li
                      v-for="f in [
                        'Tudo da LIA 1.4',
                        'Google Calendar integrado',
                        'Lembretes automáticos WhatsApp',
                        'Reagendamento inteligente via chat',
                      ]"
                      :key="f"
                      class="flex items-start gap-2.5 text-sm text-n-slate-12"
                    >
                      <span
                        class="i-lucide-check size-4 text-blue-500 mt-0.5 flex-shrink-0"
                      />
                      {{ f }}
                    </li>
                  </ul>

                  <div
                    class="flex flex-wrap gap-1.5 pt-5 border-t border-n-weak"
                  >
                    <span
                      v-for="u in [
                        'Clínicas',
                        'Consultórios',
                        'Salões',
                        'Serviços',
                      ]"
                      :key="u"
                      class="px-2.5 py-1 text-[11px] font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ u }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- LIA 3.1 -->
              <div
                class="rounded-2xl border bg-n-solid-2 overflow-hidden"
                :class="[
                  hasVersion('lia-3-1')
                    ? 'border-violet-300 dark:border-violet-800/60'
                    : 'border-n-weak',
                ]"
              >
                <div
                  v-if="hasVersion('lia-3-1')"
                  class="px-6 py-2 bg-violet-50 dark:bg-violet-900/20 flex items-center gap-2"
                >
                  <span
                    class="i-lucide-check-circle size-4 text-violet-600 dark:text-violet-400"
                  />
                  <span
                    class="text-xs font-semibold text-violet-700 dark:text-violet-300"
                  >
                    Ativo no seu plano
                  </span>
                </div>
                <div class="p-6">
                  <div class="flex items-center justify-between mb-4">
                    <span
                      class="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300"
                    >
                      EXPERT
                    </span>
                    <div class="text-right">
                      <span class="text-sm font-bold text-n-slate-12"
                        >R$ 997</span
                      >
                      <span class="text-xs text-n-slate-10">/mês</span>
                    </div>
                  </div>
                  <h3 class="text-xl font-bold text-n-slate-12 mb-1">
                    LIA 3.1
                  </h3>
                  <p class="text-sm text-n-slate-11 mb-6">Agente Expert</p>

                  <ul class="space-y-2.5 mb-6">
                    <li
                      v-for="f in [
                        'Tudo da LIA 2.1',
                        'Integração completa com ERPs',
                        'Ações e consultas em tempo real',
                        'Multi-agente especializado',
                      ]"
                      :key="f"
                      class="flex items-start gap-2.5 text-sm text-n-slate-12"
                    >
                      <span
                        class="i-lucide-check size-4 text-violet-500 mt-0.5 flex-shrink-0"
                      />
                      {{ f }}
                    </li>
                  </ul>

                  <div
                    class="flex flex-wrap gap-1.5 pt-5 border-t border-n-weak"
                  >
                    <span
                      v-for="u in [
                        'Provedores',
                        'Telecom',
                        'Enterprise',
                        'Financeiro',
                      ]"
                      :key="u"
                      class="px-2.5 py-1 text-[11px] font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ u }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Benefits -->
            <div
              class="grid grid-cols-4 gap-px bg-n-weak rounded-2xl overflow-hidden mb-10"
            >
              <div
                v-for="b in [
                  {
                    icon: 'i-lucide-clock',
                    title: 'Atendimento 24/7',
                    desc: 'Sua IA atende a qualquer hora, sem pausas.',
                  },
                  {
                    icon: 'i-lucide-zap',
                    title: 'Respostas em <3s',
                    desc: 'Velocidade que impressiona seus clientes.',
                  },
                  {
                    icon: 'i-lucide-brain',
                    title: 'IA que Aprende',
                    desc: 'Quanto mais usa, mais inteligente fica.',
                  },
                  {
                    icon: 'i-lucide-trending-up',
                    title: 'ROI Comprovado',
                    desc: '+50% vendas, -70% custos operacionais.',
                  },
                ]"
                :key="b.title"
                class="bg-n-solid-2 p-6 text-center"
              >
                <span
                  :class="b.icon"
                  class="size-6 text-n-slate-11 mx-auto mb-3 block"
                />
                <h4 class="text-sm font-semibold text-n-slate-12 mb-1">
                  {{ b.title }}
                </h4>
                <p class="text-xs text-n-slate-11 leading-relaxed">
                  {{ b.desc }}
                </p>
              </div>
            </div>

            <!-- CTA -->
            <div
              class="rounded-2xl bg-woot-800 dark:bg-woot-900 p-10 text-center mb-6"
            >
              <h3 class="text-xl font-bold text-white mb-2">
                Quer turbinar seu atendimento?
              </h3>
              <p class="text-sm text-woot-200 mb-5 max-w-md mx-auto">
                Faça upgrade ou adicione novos agentes. Fale com nosso time.
              </p>
              <div
                class="flex items-center justify-center gap-5 text-xs text-woot-200"
              >
                <span class="flex items-center gap-1.5">
                  <span class="i-lucide-check size-3.5 text-green-400" />
                  Setup em 24h
                </span>
                <span class="flex items-center gap-1.5">
                  <span class="i-lucide-check size-3.5 text-green-400" />
                  Suporte dedicado
                </span>
                <span class="flex items-center gap-1.5">
                  <span class="i-lucide-check size-3.5 text-green-400" />
                  Sem fidelidade
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Delete Modal -->
    <woot-modal v-model:show="showDeleteModal" :on-close="closeDeleteModal">
      <div class="flex flex-col p-8">
        <h2 class="text-lg font-semibold text-n-slate-12 mb-2">
          {{ t('LIA_AGENTS.LIST.DELETE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-4">
          {{
            t('LIA_AGENTS.LIST.DELETE_CONFIRM', {
              name: deleteTarget?.name || '',
            })
          }}
        </p>
        <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
          {{ t('LIA_AGENTS.LIST.DELETE_PASSWORD_LABEL') }}
        </label>
        <input
          v-model="deletePassword"
          type="password"
          class="w-full px-3 py-2.5 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all mb-6"
          :placeholder="t('LIA_AGENTS.LIST.DELETE_PASSWORD_PLACEHOLDER')"
          @keyup.enter="confirmDelete"
        />
        <div class="flex items-center justify-end gap-2">
          <button
            class="px-4 py-2 text-sm font-medium rounded-xl border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="closeDeleteModal"
          >
            {{ t('LIA_AGENTS.LIST.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium rounded-xl text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
            :disabled="!deletePassword || isDeleting"
            @click="confirmDelete"
          >
            <span
              v-if="isDeleting"
              class="i-lucide-loader-2 w-4 h-4 animate-spin inline-block mr-1"
            />
            {{ t('LIA_AGENTS.LIST.DELETE') }}
          </button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
